import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styles from './UsageAnalytics.module.scss';
import Header from 'src/components/header/Header';
import TabLayout from './tabLayout/TabLayout';
import { SelectOption, UsageEnum, UserRoleEnum } from '@src/types';
import { useQueryDictionary, useRolesPermissions, useUser } from '@src/hooks';
import moment from 'moment';
import { newAUStandaloneLessonAgeGroupOptions, NAStandaloneLessonAgeGroupOptions } from '@src/constants';
import UsageView from './UsageView/UsageView';
import EngagementView from './EngagementView/EngagementView';
import SearchLayout from './filterLayout/SearchLayout';
import CloseIcon from 'src/assets/icons/close.svg?react';
import IconRotating from 'src/assets/icons/rotating_arrow.svg?react';
import Button from '@src/components/button/Button';

function UsageAnalytics() {

  const [currentMode, setCurrentMode] = useState(UsageEnum.Usage)
  const isDistrictAdmin = useRolesPermissions([UserRoleEnum.DistrictAdmin]);
  const [filters, setFilters] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  const [user] = useUser();
  const allSchools = useQueryDictionary({dictionary: 'Schools'});

  const isAussie = useMemo(() => user?.id?.startsWith('AU_'), [user?.id]);

  const [focused, setFocused] = useState(false);
  const [showGradeMenu, setShowGradeMenu] = useState(false);
  const [showSchoolMenu, setShowSchoolMenu] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [grade, setGrade] = useState<SelectOption | undefined>();
  const [school, setSchool] = useState<SelectOption>({ label: "All Schools", value: "All Schools" });

  const [gradeResult, setGradeResult] = useState<SelectOption | undefined>();
  const [searchResult, setSearchResult] = useState('');

  const today = moment();
  const startYear = today.month() < 7 ? today.year() - 1 : today.year(); // If current month is before August (0-6), use last year
  const startingDate = isAussie ? new Date(startYear, 0, 21) : new Date(startYear, 7, 1);   // August 1 for NA schools, Jan 21st for Aus Schools

  const [dateRange, setDateRange] = useState<any[]>([startingDate, new Date()]);

  useEffect(() => {
    if (user?.id?.startsWith('AU_')) {
      const today = moment();
      const startingDate = new Date(today.year(), 0, 21);   // Jan 21st for Aus Schools
      setDateRange([startingDate, new Date()])
    } else {
      const today = moment();
      const startYear = today.month() < 7 ? today.year() - 1 : today.year(); // If current month is before August (0-6), use last year
      const startingDate = new Date(startYear, 7, 1);   // August 1 for NA schools,
      setDateRange([startingDate, new Date()])
    }
  }, [user?.id])

  const allSchoolOptions = useMemo(() => {
    const allowedSchoolIds = user?.districtSchools ? JSON.parse(user.districtSchools) : [];
    const _allSchoolOptions = (allSchools ?? []).filter(s => !!s?.value && allowedSchoolIds.includes(s.value)).map((s) => {
      return {
        value: s!.value,
        label: s!.label,
      };
    });
    _allSchoolOptions.unshift({label: "All Schools", value: "All Schools"})
    return _allSchoolOptions
  }, [allSchools, user?.districtSchools])

  const filterSchools = useMemo(() => {
    if (isDistrictAdmin) {
      if (school?.value === 'All Schools') {
        return allSchoolOptions.map(option => option.value).filter(school =>  school !== 'All Schools')
      } else {
        return [school?.value ?? '']
      }
    }
    return user?.schools?.filter(sc => !!sc?.id).map(sc => sc?.id as string ) ?? []
  }, [allSchoolOptions, school, user?.schools, isDistrictAdmin])

  const filterGrades = useMemo(() => {
    const filterGrades = isAussie ? newAUStandaloneLessonAgeGroupOptions : NAStandaloneLessonAgeGroupOptions;
    if (gradeResult) {
      const gradeValues = filterGrades.map(program => {
        if (program.label === gradeResult.label) {
          return program.value
        } else {
          return ""
        }
      })
      return gradeValues.filter(Boolean)
    }
    return filterGrades.map(grade => grade.label);
  }, [gradeResult, isAussie])

  const handleSelectGrade = useCallback((grade: SelectOption) => {
    setGrade(grade);
  }, [])

  const handleSelectSchool = useCallback((school: SelectOption) => {
    setSchool(school);
  }, [])

  const handleChangeDate = useCallback((dateString: any[]) => {
    if (dateString.length > 0) {
      setDateRange(dateString)
    }
  }, []);

  const handleSelectUsageMode = useCallback((mode: UsageEnum) => {
    setCurrentMode(mode)
  }, [])

  const handleResetFilters = useCallback(() => {
    setFilters([]);
    setGrade(undefined);
    setSearch('');
    setGradeResult(undefined);
    setSearchResult('');
  }, []);

  const handleShowFilters = () => {
    setShowDatePicker(current => !current);
  }

  const handleChangeKeyword = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const handleFilterApply = useCallback(() => {
    var filterOptions: string[] = []
    if (grade) {
      setGradeResult(grade);
      filterOptions.push(`Grade: ${grade?.label}`)
    }

    if (search) {
      setSearchResult(search);
      filterOptions.push(`Keyword: ${search}`)
    }
    setFilters(filterOptions);
    setShowFilterMenu(false);
  }, [grade, search])

  const handleClickDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowFilterMenu(false);
  }

  const handleRemoveFilters = useCallback((value: string[]) => {
    const ageGroups = value.filter(group => group.includes('Grade:'))
    if (ageGroups.length > 0) {
      setGrade(undefined)
      setGradeResult(undefined)
    }

    const changedKeyword = value.filter(group => group.includes('Keyword:'))
    if (changedKeyword.length > 0) {
      setSearch('')
      setSearchResult('');
    }

    if (value.length === 0) {
      handleResetFilters();
    } else {
      setFilters(value);
    }
  }, [handleResetFilters])

  const handleRemoveTag = (removedTag: string) => {
    const newTags = filters.filter((tag) => tag !== removedTag);
    handleRemoveFilters(newTags);
  };

  const handleDatePicker = (open: boolean) => {
    setShowDatePicker(open);
  }
  const searchViewStyle = useMemo(() => {
    const classes = [styles.searchView];
    // remove search bar
    // if (showFilterMenu) {
    //   classes.push(styles.filterShow);
    // }
    return classes.join(' ');
  }, [])

  const currentPage = useMemo(() => {
    switch(currentMode) {
      case UsageEnum.Usage:
        return <UsageView
          grade={gradeResult} school={school} filterGrades={filterGrades} isAussie={isAussie} isDistrictAdmin={isDistrictAdmin}
          startDate={moment.utc(dateRange[0]).format('YYYY-MM-DD HH:mm:ss')} endDate={moment.utc(dateRange[1]).format('YYYY-MM-DD HH:mm:ss')} filterSchools={filterSchools}
          dateRange={dateRange} datePickerVisible={showDatePicker}
        />;
      case UsageEnum.Engagement:
        return <EngagementView
          grade={gradeResult} school={school} filterGrades={filterGrades} isAussie={isAussie}
          startDate={moment.utc(dateRange[0]).format('YYYY-MM-DD HH:mm:ss')} endDate={moment.utc(dateRange[1]).format('YYYY-MM-DD HH:mm:ss')} filterSchools={filterSchools}
          dateRange={dateRange} datePickerVisible={showDatePicker} keyword={searchResult}
        />;
      default:
        return null;
    }
  }, [currentMode, showDatePicker, dateRange, gradeResult, school, isDistrictAdmin, isAussie, filterSchools, filterGrades, searchResult])

  return (
    <>
      <Header />
      <div className={styles.wrapper}>
        <div className={[styles.backdrop, showFilterMenu ? styles.mfadeIn : styles.mfadeOut].join(' ')} onClick={handleClickDrop} />
        <div className={searchViewStyle}>
          <SearchLayout
            focused={focused}
            setFocused={setFocused}
            showGradeMenu={showGradeMenu}
            setShowGradeMenu={setShowGradeMenu}
            showSchoolMenu={showSchoolMenu}
            setShowSchoolMenu={setShowSchoolMenu}
            showDatePicker={showDatePicker}
            setShowDatePicker={handleDatePicker}
            filters={filters}
            search={search}
            gradeOptions={isAussie ? newAUStandaloneLessonAgeGroupOptions : NAStandaloneLessonAgeGroupOptions}
            selGrade={grade}
            startDate={dateRange[0]}
            endDate={dateRange[1]}
            dateRange={dateRange}
            onDateChange={handleChangeDate}
            handleSelectGrade={handleSelectGrade}
            handleSelectSchool={handleSelectSchool}
            handleChangeKeyword={handleChangeKeyword}
            handleApplySearch={handleFilterApply}
          />
        </div>
        {(focused || showGradeMenu || showSchoolMenu || showDatePicker) && <div className={[styles.menuBackdrop, showFilterMenu ? styles.mfadeIn : styles.mfadeOut].join(' ')} />}
        <div className={styles.page}>
          <div className={styles.leftView}>
            <TabLayout
              usageMode={currentMode}
              showFilterMenu={showFilterMenu}
              handleSelectUsageMode={handleSelectUsageMode}
              handleFilterClick={handleShowFilters}
              filters={filters}
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              dateRange={dateRange}
              onDateChange={handleChangeDate}
              showDatePicker={showDatePicker}
              setShowDatePicker={handleDatePicker}
              showSchoolMenu={showSchoolMenu}
              setShowSchoolMenu={setShowSchoolMenu}
              selSchool={school}
              schoolOptions={allSchoolOptions}
              handleSelectSchool={handleSelectSchool}
              />
            <div className={styles.mainView}>
              <div style={{display: 'none'}}>
              {filters.length > 0 ?
                <div className={styles.searchResult}>
                  <div className={styles.searchResultTag}>
                    <span className={styles.resultTitle}>Results for</span>
                      <div className={styles.searchedTags}>
                      {filters.map((tag, index) => {
                        if (tag.includes('Grade')) {
                          var grade = tag.replace('Grade: ', '')
                          return <div
                            className={styles.searchTag}
                            key={index}
                            onClick={() => handleRemoveTag(tag)}>
                              <span>{grade}</span>
                              <CloseIcon className={styles.tagClose}/>
                          </div>
                        }
                        const keyword = tag.replace('Keyword: ', '')
                        const isLongTag = keyword.length > 30;
                        return <div
                          className={styles.searchThemeTag}
                          key={index}
                          onClick={() => handleRemoveTag(tag)}
                          >
                            <span>{isLongTag ? `${keyword.slice(0, 30)}...` : keyword}</span>
                            <CloseIcon className={styles.themeTagClose}/>
                        </div>
                        })
                      }
                    </div>
                  </div>
                  <Button onClick={handleResetFilters} className={styles.resetBtn} label={'Reset'}
                    size={'extraSmall'} outline={true} color={'red'}
                    leftIcon={<IconRotating className={styles.resetIcon}/>}/>
                </div> : null
              }
              </div>
              {allSchoolOptions && user && currentPage}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default UsageAnalytics;
